<!--
    "מחיקת לקוח" - mark the פניה as not relevant, or really delete the customer.

    Opened from the customer card's מחיקת לקוח link and from the trash on the /customers table, through
    customerHandler.askDeleteCustomer (one instance, mounted in App.vue). It does what is chosen itself
    and resolves what happened: 'notRelevant' | 'deleted' | null (closed).

    סימון כלא רלוונטית   the פניה shown (the card's chosen event / the table row's event) goes to פניה לא
                         רלוונטית - or הזמנה מבוטלת when an order already went out, as in
                         ChangeEventStatus - with an optional reason (the account's canceling statuses).
                         The customer stays; the event leaves the calendar.
    מחיקת הלקוח          to the archive (/customers-archive, restorable): scope 'customer' archives every
                         event of the customer (customers/delete), scope 'row' the table row only
                         (search-results/delete). Archived events leave the calendar and Google Calendar.

    A customer with tax documents can't be deleted (the api refuses too) - the popup says so and offers
    the first option only.
-->
<template>
    <Modal ref="modal"
           class="deleteCustomerModal"
           :center="true"
           title="מחיקת לקוח">

        <template v-slot:body>

            <div v-if="loading"
                 class="dcmLoading">
                <i class="fa-solid fa-spinner fa-spin"></i>
                טוען...
            </div>

            <div v-else>

                <div class="dcmQuestion">{{ question }}</div>

                <div v-if="customerNameLine"
                     class="dcmCustomer">
                    <i class="fa-regular fa-user"></i>
                    {{ customerNameLine }}
                </div>


                <!-- סימון הפניה כלא רלוונטית -->
                <div v-if="event"
                     class="dcmOption"
                     :class="{ dcmOptionDone: alreadyNotRelevant }">

                    <div class="dcmIcon dcmIconNotRelevant">
                        <i class="fa-solid fa-ban"></i>
                    </div>

                    <div class="dcmOptionBody">

                        <div class="dcmOptionTitle">סימון הפניה כלא רלוונטית</div>

                        <div v-if="eventLine"
                             class="dcmEventLine">
                            {{ eventLine }}
                        </div>

                        <div v-if="alreadyNotRelevant"
                             class="dcmOptionText">
                            הפניה כבר מסומנת:
                            <span v-html="$handlers.eventHandler.systemStatusPillHTML(openingStatus)"></span>
                        </div>

                        <template v-else>

                            <div class="dcmOptionText">
                                <template v-if="hasOrder">
                                    לאירוע כבר יצאה הזמנה - היא תסומן כמבוטלת והאירוע יירד מהיומן. הלקוח נשאר במערכת.
                                </template>
                                <template v-else>
                                    הלקוח נשאר במערכת עם הסטטוס "פניה לא רלוונטית" והאירוע יורד מהיומן. אפשר להחזיר אותו לטיפול בכל עת.
                                </template>
                            </div>


                            <!-- the account's canceling statuses (סטטוס תהליך פנימי) are its reasons -->
                            <div v-if="reasons.length"
                                 class="dcmReason">

                                <label class="form-label">סיבה</label>

                                <Multiselect v-model="reason"
                                             :options="reasons"
                                             label="name"
                                             track-by="id"
                                             :allow-empty="true"
                                             :disabled="!!working"
                                             :prevent-autofocus="true"
                                             class="whitebg_multiselect"
                                             selectedLabel=""
                                             deselect-label=""
                                             selectLabel=""
                                             placeholder="בחרו סיבה (לא חובה)">
                                    <template slot="noResult">לא נמצאו תוצאות</template>
                                    <template slot="noOptions">אין אפשרויות</template>
                                </Multiselect>
                            </div>


                            <!-- the rest of a cancelation, as ChangeEventStatus offers it -->
                            <div v-if="hasOrder || event.howMuchWasPaid"
                                 class="dcmFollowUps">

                                <label v-if="hasOrder"
                                       class="dcmFollowUp"
                                       @click="makeHazmanatBitul = !makeHazmanatBitul">

                                    <span class="planup-checkbox planup-checkbox-purple"
                                          :class="{ checked: makeHazmanatBitul }">
                                        <i class="fa fa-check"></i>
                                    </span>

                                    המשך להפקת הזמנת ביטול
                                    ({{ openingStatus == 'hazmanaSigned' ? 'קיימת הזמנה חתומה' : 'יצאה הזמנה' }})
                                </label>

                                <label v-if="event.howMuchWasPaid"
                                       class="dcmFollowUp"
                                       @click="makeNegativeKabala = !makeNegativeKabala">

                                    <span class="planup-checkbox planup-checkbox-purple"
                                          :class="{ checked: makeNegativeKabala }">
                                        <i class="fa fa-check"></i>
                                    </span>

                                    המשך להוצאת קבלה שלילית
                                    (התקבל תשלום על סך <strong>&#8362;{{ event.howMuchWasPaid }}</strong>)
                                </label>
                            </div>


                            <div class="dcmActions">
                                <button type="button"
                                        id="deleteCustomerModalNotRelevant"
                                        class="btn btn-primary"
                                        :disabled="!!working"
                                        @click="markNotRelevant">
                                    <i v-if="working == 'notRelevant'"
                                       class="fa-solid fa-spinner fa-spin"></i>
                                    {{ makeHazmanatBitul || makeNegativeKabala ? 'סימון והמשך' : 'סימון כלא רלוונטית' }}
                                </button>
                            </div>

                        </template>

                    </div>
                </div>


                <!-- מחיקת הלקוח - not offered at all when the customer has tax documents (the question says why) -->
                <div v-if="canDelete"
                     class="dcmOption">

                    <div class="dcmIcon dcmIconDelete">
                        <i class="fa-regular fa-trash-can"></i>
                    </div>

                    <div class="dcmOptionBody">

                        <div class="dcmOptionTitle">מחיקת הלקוח</div>

                        <div class="dcmOptionText">{{ deleteText }}</div>

                        <div class="dcmActions">
                            <button type="button"
                                    id="deleteCustomerModalDelete"
                                    class="btn btn-light"
                                    :disabled="!!working"
                                    @click="remove">
                                <i :class="working == 'delete' ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-trash-can'"></i>
                                מחיקת הלקוח
                            </button>
                        </div>

                    </div>
                </div>


                <div v-if="error"
                     class="dcmError">
                    {{ error }}
                </div>

            </div>

        </template>

        <template v-slot:buttons>
            <button type="button"
                    class="btn btn-light"
                    :disabled="!!working"
                    @click="cancel">
                ביטול
            </button>
        </template>

    </Modal>
</template>

<script>

import Multiselect from 'vue-multiselect';

export default {
    name: 'DeleteCustomerModal',

    components: { Multiselect },

    data() {
        return {
            scope: 'customer',          //'customer' (the card: every event) | 'row' (the /customers table: one row)
            customerId: null,
            searchResultId: null,       //scope 'row'
            contactNames: [],
            event: null,                //the פניה to mark - null for a table row with no event
            openingStatus: null,        //its מצב פניה when the popup opened - marking changes the event at once
            eventsCount: 0,             //scope 'customer': how many events go to the archive with it
            taxDocumentsCount: 0,
            reason: null,               //a canceling planupEventStatus, optional
            makeHazmanatBitul: false,
            makeNegativeKabala: false,
            loading: false,
            working: '',                //'' | 'notRelevant' | 'delete'
            error: '',
            resolvePromise: null,
            shownForAsk: false,         //this ask's own show() went through - see mounted()
            retryTimer: null,
        }
    },

    computed: {

        // the account's canceling statuses - picking one is how ChangeEventStatus marks a פניה לא רלוונטית
        reasons() {
            return (this.$store.state.planupEventStatuses || []).filter(status => status.cancelsPnia && !status.hidden);
        },

        // both read the status the popup opened with: marking sets the event's status before the save
        // returns, and the popup would flip to "already marked" while it closes
        hasOrder() {
            return !!this.event && (this.openingStatus == 'hazmanaSent' || this.openingStatus == 'hazmanaSigned');
        },

        alreadyNotRelevant() {
            return !!this.event && (this.openingStatus == 'pniaNotRelevant' || this.openingStatus == 'hazmanaCanceled');
        },

        canDelete() {
            return !this.taxDocumentsCount;
        },

        question() {

            if (!this.canDelete) {

                if (this.event && !this.alreadyNotRelevant)
                    return 'לא ניתן למחוק לקוח שהופקו לו מסמכים חשבונאיים, אבל אפשר לסמן את הפניה כלא רלוונטית.';

                return 'לא ניתן למחוק לקוח שהופקו לו מסמכים חשבונאיים.';
            }

            if (this.event && !this.alreadyNotRelevant)
                return 'האם לסמן את הפניה כלא רלוונטית, או ממש למחוק את הלקוח?';

            return 'האם למחוק את הלקוח?';
        },

        // "ליאור לוי ושגיב לוי" / "א, ב וג"
        customerNameLine() {

            let names = (this.contactNames || [])
                .map(name => String(name || '').replace(/\s+/g, ' ').trim())
                .filter(name => name);

            if (names.length < 2)
                return names[0] || '';

            return names.slice(0, -1).join(', ') + ' ו' + names[names.length - 1];
        },

        eventLine() {

            if (!this.event)
                return '';

            let parts = [];

            if (this.event.name)
                parts.push(this.event.name);

            if (this.event.date)
                parts.push(this.$globals.niceDate(this.event.date));

            return parts.join(' · ');
        },

        deleteText() {

            if (this.scope == 'customer' && this.eventsCount > 1)
                return `הלקוח ו-${this.eventsCount} האירועים שלו יעברו לארכיון ויוסרו מטבלת הלקוחות ומהיומן. אפשר לשחזר אותם מהארכיון.`;

            return 'הלקוח יעבור לארכיון ויוסר מטבלת הלקוחות ומהיומן. אפשר לשחזר אותו מהארכיון.';
        },
    },

    watch: {

        // one instance for the whole app (App.vue) - leaving the page (eg. the browser's back button
        // with the popup up) closes it unanswered instead of leaving it over the next page
        '$route.path'() {

            if (!this.resolvePromise)
                return;

            this.settle(null);
            this.hideModal();
        },
    },

    mounted() {

        let el = this.$refs.modal.$el;

        //the X, Esc and the backdrop close the modal without an answer - the promise must still settle.
        //Only after this ask's own show, though: a fade-out that was still running when the popup was
        //asked for again (reopened right after closing) ends with a hidden event of its own
        el.addEventListener('show.bs.modal', () => { this.shownForAsk = true; });
        el.addEventListener('hidden.bs.modal', () => { if (this.shownForAsk) this.settle(null); });
    },

    methods: {

        // options: { scope, customerId, searchResultId, contactNames: [], event, eventsCount }
        ask(options) {

            this.settle(null);

            this.scope = options.scope || 'customer';
            this.customerId = options.customerId || null;
            this.searchResultId = options.searchResultId || null;
            this.contactNames = options.contactNames || [];
            this.event = options.event || null;
            this.openingStatus = this.event ? this.event.planupSystemStatus : null;
            this.eventsCount = options.eventsCount || 0;
            this.taxDocumentsCount = 0;
            this.makeHazmanatBitul = false;
            this.makeNegativeKabala = false;
            this.working = '';
            this.error = '';

            // a פניה already parked on a canceling status keeps it. Nothing is picked for the user
            // otherwise - even a lone reason is a specific one ("סגרו דיג׳יי אחר") and would be
            // recorded without being chosen
            let current = this.event && this.event.planupEventStatus;
            this.reason = (current && this.reasons.find(status => status.id == current.id)) || null;

            this.loading = !!this.customerId;

            // asked again while it is up: its next hidden event is this ask's
            this.shownForAsk = this.$refs.modal.$el.classList.contains('show');

            clearTimeout(this.retryTimer);
            this.$refs.modal.show();

            // bootstrap ignores show() while a fade-out is still running - try again once it can't be
            this.retryTimer = setTimeout(() => {
                if (this.resolvePromise && this.$refs.modal && !this.$refs.modal.$el.classList.contains('show'))
                    this.$refs.modal.show();
            }, 400);

            if (this.customerId) {

                // a failed check falls back to "may delete" - the api refuses a customer with tax
                // documents anyway, and the refusal shows up here
                this.$handlers.customerHandler.hasTaxDocuments(this.customerId).then(result => {
                    this.taxDocumentsCount = (result && result.count) || 0;
                    this.loading = false;
                }).catch(() => {
                    this.loading = false;
                });
            }

            return new Promise(resolve => { this.resolvePromise = resolve });
        },


        markNotRelevant() {

            if (this.working || !this.event || this.alreadyNotRelevant)
                return;

            let event = this.event;
            let customerId = this.customerId;
            let makeHazmanatBitul = this.hasOrder && this.makeHazmanatBitul;
            let makeNegativeKabala = !!event.howMuchWasPaid && this.makeNegativeKabala;

            this.working = 'notRelevant';
            this.error = '';

            // an order that already went out is canceled, not "not relevant" - as in ChangeEventStatus
            this.$handlers.eventHandler.applyStatusChange(event, this.hasOrder ? 'hazmanaCanceled' : 'pniaNotRelevant', this.reason || null).then(() => {

                this.working = '';
                this.finish('notRelevant');

                this.$handlers.eventHandler.continueCancelingProcess(customerId, event.id, makeHazmanatBitul, makeNegativeKabala);

            }).catch(() => {
                this.working = '';
                this.error = 'העדכון לא נשמר - נסו שוב';
            });
        },


        remove() {

            if (this.working || !this.canDelete)
                return;

            this.working = 'delete';
            this.error = '';

            let request = this.scope == 'row'
                ? this.$handlers.customerHandler.deleteSearchResult(this.searchResultId, this.event && this.event.id)
                : this.$handlers.customerHandler.deleteCustomer(this.customerId);

            request.then(() => {

                this.working = '';

                // the archived events are gone from the calendar - drop the months it already holds
                this.$handlers.calendarHandler.hardResetAllCalendars();

                this.finish('deleted');

            }).catch(error => {

                this.working = '';

                let message = error && error.response && error.response.data && error.response.data.message;
                this.error = typeof message == 'string' && message ? message : 'המחיקה נכשלה - נסו שוב';
            });
        },


        cancel() {
            this.finish(null);
        },


        // the answer is known the moment a button worked, so report it right away - then make sure the
        // modal really closes
        finish(answer) {
            this.settle(answer);
            this.hideModal();
        },

        hideModal() {

            clearTimeout(this.retryTimer);
            this.$refs.modal.hide();

            // bootstrap's hide() is a silent no-op while the fade-IN is still running (a click within
            // ~300ms of opening) - try again once the transition can't be running, unless the popup
            // was asked for again meanwhile
            this.retryTimer = setTimeout(() => {
                if (!this.resolvePromise && this.$refs.modal && this.$refs.modal.$el.classList.contains('show'))
                    this.$refs.modal.hide();
            }, 400);
        },

        settle(answer) {

            if (!this.resolvePromise)
                return;

            let resolve = this.resolvePromise;
            this.resolvePromise = null;
            resolve(answer);
        },
    },
}
</script>

<style>
/* not scoped: Modal renders the dialog, so the width rule has to reach into it */
.deleteCustomerModal .modal-dialog {
    max-width: 560px;
}

.deleteCustomerModal .dcmLoading {
    text-align: center;
    padding: 30px 0;
    color: #777;
}

.deleteCustomerModal .dcmQuestion {
    font-size: 17px;
    font-weight: 600;
    color: #333;
    line-height: 1.5;
}

.deleteCustomerModal .dcmCustomer {
    margin-top: 6px;
    font-size: 14px;
    color: #777;
}

.deleteCustomerModal .dcmOption {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-top: 14px;
    padding: 16px;
    border: solid 1px #eeeeee;
    border-radius: 10px;
    background: #fff;
    transition: border-color .15s, box-shadow .15s;
}

.deleteCustomerModal .dcmOption:hover {
    border-color: #e0e0e0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, .05);
}

.deleteCustomerModal .dcmIcon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
}

/* the פניה לא רלוונטית pill's colors */
.deleteCustomerModal .dcmIconNotRelevant {
    background: #ffecef;
    color: #e63e5c;
}

/* the app's plain gray trash */
.deleteCustomerModal .dcmIconDelete {
    background: #f1f2f6;
    color: #999;
}

.deleteCustomerModal .dcmOptionBody {
    flex: 1;
    min-width: 0;
}

.deleteCustomerModal .dcmOptionTitle {
    font-size: 15px;
    font-weight: 600;
    color: #333;
}

.deleteCustomerModal .dcmEventLine {
    margin-top: 2px;
    font-size: 13px;
    color: #555;
}

.deleteCustomerModal .dcmOptionText {
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.6;
    color: #777;
}

.deleteCustomerModal .dcmReason {
    margin-top: 12px;
}

.deleteCustomerModal .dcmReason .form-label {
    font-size: 13px;
    margin-bottom: 4px;
}

.deleteCustomerModal .dcmFollowUps {
    margin-top: 12px;
}

.deleteCustomerModal .dcmFollowUp {
    display: block;
    margin: 6px 0;
    font-size: 13px;
    cursor: pointer;
    user-select: none;
}

.deleteCustomerModal .dcmFollowUp .planup-checkbox {
    position: relative;
    top: 1px;
}

.deleteCustomerModal .dcmActions {
    margin-top: 14px;
    display: flex;
    justify-content: flex-end;
}

.deleteCustomerModal .dcmActions .btn i {
    margin-left: 4px;
}

.deleteCustomerModal .dcmError {
    margin-top: 12px;
    font-size: 13px;
    color: #e63e5c;
}
</style>
